/**
 * AssignedWorkInbox — «НАДАД ИРСЭН АЖИЛ» (аюулын мэдээллээс хуваарилагдсан).
 *
 * Хоёр бүртгэлийн хуудсанд ашиглагдана:
 *   /highrisk/register  — workType="OEA"     (ӨЭА наряд)
 *   /naryad/register    — workType="NARYAD"  (ажлын даалгавар)
 *
 * ⚠ ЯАГААД БҮРТГЭЛИЙН ХУУДСАНД ГАРГАВ:
 *   ХАБ хэлтэс аюулыг хуваарилахдаа «энэ бол ӨЭА-гийн ажил» гэж заасан ч
 *   хариуцагч нь тэр мэдээллийг ОГТ ХАРАХГҮЙ байсан. Ажил үүсгэдэг газар
 *   нь энэ хуудас тул даалгавар ЯГ ЭНД харагдах ёстой — өөр хуудас руу
 *   явж хайх алхам нэмэгдэх бүрд гүйцэтгэлийн магадлал буурна.
 *
 * ⚠ Ажил үүсгэсэн (`assigned_work_job_id`) мөрүүд ЭНД ГАРАХГҮЙ — нэг
 *   аюулаас хоёр наряд үүсгэхээс сэргийлнэ (backend `pending_only`).
 *
 * ⚠ `onCreate` ӨГӨГДВӨЛ мөр бүрд «Ажил үүсгэх» товч гарна. Эцэг хуудас нь
 *   өөрийн үүсгэх цонхыг аюулын мэдээллээр УРЬДЧИЛАН БӨГЛӨЖ нээнэ. Ингэж
 *   хийсний шалтгаан: хэрэглэгч ажлаа тусад нь үүсгээд «аль аюулаас
 *   үүдэлтэй вэ» гэдгийг гараар холбохыг найдвал ихэнхдээ мартана —
 *   тэгвэл даалгавар хаагдахгүй, банер мөнхөд үлдэнэ.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Tag, Tooltip } from "antd";

import { getHazards } from "src/api/hazard";

const LABEL = {
  OEA: "ӨЭА наряд",
  NARYAD: "Ажлын даалгавар",
};

/** Эрсдэлийн түвшин — /hazard/triage-тай ижил өнгө. */
const SEVERITY = {
  1: { name: "Бага", color: "#16A34A" },
  2: { name: "Дунд", color: "#CA8A04" },
  3: { name: "Өндөр", color: "#EA580C" },
  // ⚠ ХУУЧИН 4/5 — сонголтод ГАРАХГҮЙ, зөвхөн баазад хоцорсон мөрийг
  //   «Өндөр» гэж зөв харуулахын тулд үлдээв (ХООСОН харагдах ёсгүй).
  4: { name: "Өндөр", color: "#DC3545" },
  5: { name: "Өндөр", color: "#DC3545" },
};

export default function AssignedWorkInbox({
  workType,
  style,
  // Мөрөөс шууд ажил үүсгэх — эцэг хуудас өөрийн цонхоо нээнэ
  onCreate,
  // Эцэг хуудас ажил үүсгэсний дараа энэ тоог өсгөж жагсаалтыг шинэчилнэ
  reloadKey,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  /* ⚠ Анхнаасаа ХУРААСАН (2026-09-26): хуваарилагдсан ажил олон үед
     жагсаалт нь бүтэн дэлгэц эзэлж, хуудасны үндсэн агуулгыг доош
     түлхдэг байв. Тоо нь толгой мөрөндөө харагдана — дэлгэрэнгүйг
     хэрэгтэй үед нь задална. */
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getHazards({
      work_type: workType,
      assigned_mine: 1,
      pending_only: 1,
      hazards_only: 1,
      per_page: 50,
    })
      .then((d) => setItems(d?.items || d?.data || []))
      // ⚠ Алдааг ЗАЛГИНА: энэ бол нэмэлт блок. Татагдахгүй бол
      //   бүртгэлийн хуудас өөрөө ажиллаж байх ёстой.
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [workType]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, reloadKey]);

  // Ажил ирээгүй бол блок огт гарахгүй — хоосон хайрцаг зай эзэлнэ
  if (loading || !items.length) return null;

  const overdue = items.filter(
    (it) => it.sla_deadline && new Date(it.sla_deadline) < new Date(),
  ).length;

  return (
    <Alert
      type={overdue ? "error" : "warning"}
      showIcon
      /* ⚠ Мэдээллийн чанартай — ЖИЖИГ, анхнаасаа хураасан (2026-09-26).
         Дэлгэрэнгүй нь хэрэгтэй үед л задарна. */
      style={{ marginBottom: 8, padding: "5px 9px", ...style }}
      message={
        <span style={{ fontWeight: 700, fontSize: 11.5 }}>
          Танд {items.length} {LABEL[workType] || "ажил"} хуваарилагдсан байна
          {overdue ? ` · ${overdue} нь хугацаа хэтэрсэн` : ""}
          <Button
            type="link"
            size="small"
            onClick={() => setOpen((v) => !v)}
            style={{ paddingInline: 6 }}
          >
            {open ? "хураах" : "дэлгэрэнгүй"}
          </Button>
        </span>
      }
      description={
        open ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {items.map((it) => {
              const late =
                it.sla_deadline && new Date(it.sla_deadline) < new Date();
              const sev = SEVERITY[it.severity] || SEVERITY[2];
              return (
                <div
                  key={it.id}
                  style={{
                    background: "#fff",
                    border: "1px solid #E6E9EF",
                    // Зүүн ирмэг нь эрсдэлийн түвшнийг НЭГ ХАРЦААР хэлнэ
                    borderLeft: `3px solid ${sev.color}`,
                    borderRadius: 8,
                    padding: "8px 10px",
                  }}
                >
                  {/* Мөр 1 — тэмдэглэгээ, код, эрсдэл, хугацаа */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                      <i className="fas fa-user-check" style={{ marginRight: 4 }} />
                      Хуваарилагдсан
                    </Tag>
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontWeight: 700,
                        fontSize: 11.5,
                      }}
                    >
                      {it.report_number || `#${it.id}`}
                    </span>
                    <Tag color={sev.color} style={{ marginInlineEnd: 0 }}>
                      {sev.name}
                    </Tag>
                    {it.hazard_category_name ? (
                      <Tag style={{ marginInlineEnd: 0 }}>
                        {it.hazard_category_name}
                      </Tag>
                    ) : null}
                    {it.sla_deadline ? (
                      <Tooltip title="Энэ хугацаанд багтаан ажлаа үүсгэж гүйцэтгэнэ">
                        <Tag
                          color={late ? "red" : "gold"}
                          style={{ marginInlineEnd: 0 }}
                        >
                          {String(it.sla_deadline).slice(0, 10)} хүртэл
                          {late ? " · хэтэрсэн" : ""}
                        </Tag>
                      </Tooltip>
                    ) : null}
                  </div>

                  {/* Мөр 2 — аюулын агуулга */}
                  <div
                    style={{
                      fontSize: 12.5,
                      color: "#1F2430",
                      marginTop: 5,
                      lineHeight: 1.4,
                    }}
                  >
                    {it.description || "—"}
                  </div>

                  {/* Мөр 3 — хаана, хэн мэдээлсэн, үйлдэл */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                      marginTop: 5,
                    }}
                  >
                    <span style={{ fontSize: 11, color: "#8A94A6", flex: 1 }}>
                      {[
                        it.insert_dept_name,
                        it.location_text,
                        it.insert_user_name
                          ? `мэдээлсэн: ${it.insert_user_name}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </span>
                    {onCreate ? (
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => onCreate(it)}
                      >
                        <i className="fas fa-plus" style={{ marginRight: 5 }} />
                        Ажил үүсгэх
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      // ⚠ Хаанаас ирснээ ИЛТ дамжуулна. Энэ банер нь наряд
                      //   даалгавар ба ӨЭА хоёуланд байрладаг тул `from`
                      //   байхгүй бол дэлгэрэнгүйгээс буцахад аюулын
                      //   самбар дээр гарч ирдэг байв.
                      onClick={() =>
                        navigate(`/hazard/${it.id}`, {
                          state: { from: location.pathname + location.search },
                        })
                      }
                    >
                      Дэлгэрэнгүй
                    </Button>
                  </div>
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: "#8A6100" }}>
              «Ажил үүсгэх» дарахад маягт нь аюулын мэдээллээр урьдчилан
              бөглөгдөж нээгдэнэ. Хадгалсны дараа энэ мөр жагсаалтаас
              хасагдана — дахин үүсгэх шаардлагагүй.
            </div>
          </div>
        ) : null
      }
    />
  );
}
